import React, { useState } from 'react';
import { Button, Input, Modal, message } from 'antd';
import { submitCustomEhr } from 'api/insuranceBilling';
import useEhrConnections from 'hooks/useEhrConnections';
import s from './s.module.less';

type FormProps = {
    onCancel: () => void;
    // 提交成功后回调（宿主把该 EHR 计入 billingEhrs——后端要求 custom 默认勾选）
    onSubmitted?: (ehrName: string) => void;
};

// "Tell us about your EHR"（自建/未收录 EHR 登记）表单：EhrConnectModal 的内嵌视图与
// CustomEhrModal（settings EHR tab 的独立弹窗）共用同一份提交逻辑
export function CustomEhrForm({ onCancel, onSubmitted }: FormProps) {
    const [, , refreshConnections] = useEhrConnections();
    const [ehrName, setEhrName] = useState('');
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        const name = ehrName.trim();
        if (!name) {
            message.warning('Please enter your EHR name');
            return;
        }
        setSubmitting(true);
        const res = await submitCustomEhr({ ehrName: name, note: note.trim() || undefined });
        setSubmitting(false);
        if (res && !res.error) {
            message.success(`We've noted your EHR "${name}". Our team will reach out soon.`);
            // custom 也是一条真实连接（pending 等内部对接）：刷新后广播给所有挂载中的实例
            refreshConnections();
            onSubmitted?.(name);
        }
    };

    return (
        <>
            <div className={s.customBody}>
                <p className={s.customDesc}>
                    Built your own EHR in-house? Enter its name so we can set up your integration. Our team will reach
                    out to complete the connection, and you will get access to your workspace right away.
                </p>
                <div className={s.customLabel}>
                    EHR name<span className={s.customReq}>*</span>
                </div>
                <Input
                    size="large"
                    placeholder="e.g. Acme Health Records"
                    value={ehrName}
                    onChange={(e) => setEhrName(e.target.value)}
                />
                <div className={s.customLabel}>
                    Anything we should know? <span className={s.customOpt}>(optional)</span>
                </div>
                <Input.TextArea
                    rows={5}
                    placeholder="Tell us about your EHR, how you access it, or anything that helps us to integrate with it"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                />
            </div>
            <div className={s.customFoot}>
                <Button size="large" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    type="primary"
                    size="large"
                    onClick={handleSubmit}
                    disabled={!ehrName.trim()}
                    loading={submitting}
                >
                    Submit
                </Button>
            </div>
        </>
    );
}

type ModalProps = {
    open: boolean;
    onClose: () => void;
    onSubmitted?: (ehrName: string) => void;
};

// 独立弹窗版（settings 的 EHR tab 用；picker 里是内嵌视图切换，不走这里）
export function CustomEhrModal({ open, onClose, onSubmitted }: ModalProps) {
    return (
        <Modal
            open={open}
            onCancel={onClose}
            title="Tell us about your EHR"
            footer={null}
            width={640}
            destroyOnClose
            className={s.customModal}
        >
            <CustomEhrForm onCancel={onClose} onSubmitted={onSubmitted} />
        </Modal>
    );
}
